import React from 'react';
import GridCountCell from './grid/GridCountCell';

// =============================================================================
// ChassisChargesPanel
// =============================================================================
// The LC dashboard's "Chassis Charges" panel: a small 2x2 grid counting orders where
// GWI gets charged a chassis fee, split by where the container is sitting (GWI yard vs
// Customer) x whether it's still dropped or has been released. Mirrors the legacy
// "Chassis Charges - Dropped Customer" view's GWI/Customer x Drop/Rel shape.
//
// Purely presentational — renders the counts the backend returns and reports cell clicks
// via onSelectCell. Lives inside a <DashboardTile> (which supplies the title + drag
// handle), so it renders bare. Pairs with /lc-dashboards/chassis-charge-counts.

export interface ChassisCell {
  drop: number;
  rel: number;
}

export interface ChassisData {
  gwi: ChassisCell;
  customer: ChassisCell;
}

export interface ChassisChargesPanelProps {
  data: ChassisData | null;
  loading: boolean;
  // Fired when a non-zero count is clicked. `category` (gwi|customer) + `state` (drop|rel)
  // identify the cell for the drill-down; `title` is a ready-made label for the header.
  onSelectCell?: (category: 'gwi' | 'customer', state: 'drop' | 'rel', title: string) => void;
}

const ROWS: { key: 'gwi' | 'customer'; label: string }[] = [
  { key: 'gwi', label: 'GWI' },
  { key: 'customer', label: 'Customer' },
];
const COLS: { key: 'drop' | 'rel'; label: string }[] = [
  { key: 'drop', label: 'Drop' },
  { key: 'rel', label: 'Rel.' },
];
const EMPTY: ChassisCell = { drop: 0, rel: 0 };

const ChassisChargesPanel: React.FC<ChassisChargesPanelProps> = ({ data, loading, onSelectCell }) => {
  const placeholder = (value: number) => value === 0 && loading && !data;

  return (
    // w-full so the table fills the tile width (header + cells stretch to the panel
    // edges); natural height keeps the tile's double-click fit-to-content working.
    <div className="w-full bg-panel text-sm">
      <table className="border-collapse w-full">
        <thead>
          <tr className="bg-slate-700 text-white">
            <th className="text-left font-semibold px-2 py-1 min-w-[120px]">Chassis Charges</th>
            {COLS.map((c) => (
              <th key={c.key} className="text-right font-semibold px-2 py-1 w-[64px]">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody className="text-body">
          {ROWS.map((row) => {
            const counts = data?.[row.key] ?? EMPTY;
            return (
              <tr key={row.key} className="border-t border-gray-100 dark:border-gray-700/50">
                <td className="px-2 py-1 whitespace-nowrap text-label">{row.label}</td>
                {COLS.map((c) => {
                  const value = counts[c.key];
                  return (
                    <td key={c.key} className="border-l border-gray-100 dark:border-gray-700/50">
                      <GridCountCell
                        value={value}
                        placeholder={placeholder(value)}
                        onSelect={onSelectCell ? () => onSelectCell(row.key, c.key, `${row.label} – ${c.label}`) : undefined}
                        title={`View ${row.label} ${c.label} chassis-charge orders`}
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ChassisChargesPanel;
